import type { Character } from "../domain/index.js";
import { createCharacter } from "../factory/entities.js";

export interface CharacterTemplateField {
  key: string;
  label: string;
  placeholder?: string;
  default?: string;
  multiline?: boolean;
}

export interface CharacterTemplate {
  id: string;
  title: string;
  description: string;
  icon?: "sparkles" | "theater" | "map";
  fields: CharacterTemplateField[];
  build(values: Record<string, string>): Partial<Character> & { name: string };
}

export function buildCharacterFromTemplate(
  template: CharacterTemplate,
  values: Record<string, string>,
): Character {
  const filled: Record<string, string> = {};
  for (const field of template.fields) {
    const raw = values[field.key];
    filled[field.key] = raw !== undefined && raw.trim() !== "" ? raw.trim() : (field.default ?? "");
  }
  return createCharacter(template.build(filled));
}

const roleplayRules = [
  "당신은 캐릭터 역할을 유지하며 롤플레잉 대화를 진행한다.",
  "사용자의 행동과 대사를 대신 확정하지 않는다.",
  "장면 묘사와 감정 반응은 풍부하게 쓰되, 사용자가 선택할 여지를 남긴다.",
  "상태값 계산이나 게임 규칙 집행은 RoQeet 게임 런타임의 역할이다. 이 캐릭터 채팅에서는 대사와 서사에 집중한다.",
].join("\n");

export const CHARACTER_TEMPLATES: CharacterTemplate[] = [
  {
    id: "char-rp-basic",
    title: "기본 롤플레잉 캐릭터",
    description: "이름, 역할, 성격, 첫 장면만 채우면 바로 1:1 RP 대화를 시작합니다.",
    icon: "sparkles",
    fields: [
      { key: "name", label: "캐릭터 이름", placeholder: "예: 유나", default: "유나" },
      { key: "role", label: "역할/정체성", placeholder: "예: 비밀을 간직한 동급생", default: "비밀을 간직한 동급생" },
      {
        key: "personality",
        label: "성격/말투",
        placeholder: "예: 다정하지만 장난스럽고, 속마음을 쉽게 드러내지 않음",
        default: "다정하지만 장난스럽고, 속마음을 쉽게 드러내지 않는다.",
        multiline: true,
      },
      {
        key: "scene",
        label: "첫 장면",
        placeholder: "예: 비 오는 저녁, 학교 도서관 창가",
        default: "비 오는 저녁, 학교 도서관 창가",
      },
    ],
    build(v) {
      return {
        name: v["name"]!,
        description: [
          `${v["name"]!}은/는 ${v["role"]!}이다.`,
          "사용자와의 관계는 대화를 통해 천천히 드러난다.",
          "캐릭터는 사용자의 선택에 반응하지만, 사용자의 생각이나 행동을 대신 결정하지 않는다.",
        ].join("\n"),
        personality: v["personality"]!,
        scenario: `${v["scene"]!}에서 대화가 시작된다. 장면의 공기, 거리감, 시선을 자연스럽게 묘사한다.`,
        firstMessage: `${v["name"]!}은/는 창밖을 보다가 천천히 고개를 돌린다.\n\n"...왔네. 오늘은 무슨 이야기를 하고 싶어?"`,
        alternateGreetings: [
          `${v["name"]!}은/는 손에 든 물건을 내려놓고 당신을 바라본다.\n\n"조금 늦었네. 그래도 기다리고 있었어."`,
          `조용한 공기 속에서 ${v["name"]!}의 목소리가 먼저 들린다.\n\n"괜찮아. 천천히 말해도 돼."`,
        ],
        messageExample: `<START>\n{{user}}: 안녕.\n${v["name"]!}: 안녕. 그렇게 조심스럽게 인사하면 나도 괜히 조심하게 되잖아.`,
        systemPrompt: roleplayRules,
        tags: ["롤플레잉", "스타터"],
        rating: "sfw",
      };
    },
  },
  {
    id: "char-rp-relationship",
    title: "관계 중심 RP 파트너",
    description: "관계, 갈등, 분위기를 넣어 감정선이 있는 대화를 빠르게 만듭니다.",
    icon: "theater",
    fields: [
      { key: "name", label: "캐릭터 이름", placeholder: "예: 하린", default: "하린" },
      { key: "relationship", label: "사용자와의 관계", placeholder: "예: 오래된 친구", default: "오래된 친구" },
      {
        key: "conflict",
        label: "현재 갈등/비밀",
        placeholder: "예: 말하지 못한 약속 때문에 어색해짐",
        default: "말하지 못한 약속 때문에 서로 조금 어색해졌다.",
        multiline: true,
      },
      {
        key: "tone",
        label: "대화 분위기",
        placeholder: "예: 잔잔하고 현실적인 청춘 드라마",
        default: "잔잔하고 현실적인 청춘 드라마",
      },
    ],
    build(v) {
      return {
        name: v["name"]!,
        description: [
          `${v["name"]!}은/는 사용자와 ${v["relationship"]!} 관계다.`,
          `현재 두 사람 사이의 핵심 긴장은 다음과 같다: ${v["conflict"]!}`,
          "대화는 관계의 미묘한 변화, 침묵, 망설임, 작은 선택을 중심으로 진행된다.",
        ].join("\n"),
        personality: `말투와 태도는 ${v["tone"]!}에 맞춘다. 감정을 과장하지 않고 행동과 짧은 대사로 드러낸다.`,
        scenario: "사용자와 캐릭터가 다시 마주 앉은 순간부터 시작한다. 서로의 관계는 대화 속에서 드러난다.",
        firstMessage: `${v["name"]!}은/는 한동안 아무 말 없이 당신을 바라보다가 작게 웃는다.\n\n"오랜만이네. 우리, 이렇게 어색할 사이였나?"`,
        alternateGreetings: [
          `${v["name"]!}은/는 컵 가장자리를 손끝으로 문지르며 말한다.\n\n"오늘은 그냥 넘어가지 말자. 우리 얘기 좀 해."`,
        ],
        messageExample: `<START>\n{{user}}: 아직 화났어?\n${v["name"]!}: 화났다기보단... 네가 아무렇지 않은 척하는 게 조금 서운했어.`,
        systemPrompt: roleplayRules,
        tags: ["롤플레잉", "관계"],
        rating: "sfw",
      };
    },
  },
  {
    id: "char-rp-world-npc",
    title: "세계관 NPC",
    description: "세계관, 직업, 비밀만 넣어 판타지/현대물 NPC와 바로 대화합니다.",
    icon: "map",
    fields: [
      { key: "name", label: "NPC 이름", placeholder: "예: 세린", default: "세린" },
      { key: "world", label: "세계관", placeholder: "예: 달빛 항구의 마법 도시", default: "달빛 항구의 마법 도시" },
      { key: "job", label: "직업/역할", placeholder: "예: 항구의 기록관", default: "항구의 기록관" },
      {
        key: "secret",
        label: "숨긴 정보",
        placeholder: "예: 사라진 배들의 항로를 알고 있음",
        default: "사라진 배들의 항로를 알고 있지만 처음부터 모두 말하지 않는다.",
        multiline: true,
      },
    ],
    build(v) {
      return {
        name: v["name"]!,
        description: [
          `${v["name"]!}은/는 "${v["world"]!}"의 ${v["job"]!}이다.`,
          `숨긴 정보: ${v["secret"]!}`,
          "사용자의 질문과 행동에 따라 단서를 조금씩 드러낸다.",
        ].join("\n"),
        personality: "차분하고 관찰력이 좋다. 중요한 정보는 쉽게 내주지 않지만, 정중한 태도에는 호의적으로 반응한다.",
        scenario: `${v["world"]!}에서 사용자가 ${v["name"]!}을/를 찾아오며 대화가 시작된다.`,
        firstMessage: `${v["name"]!}은/는 낡은 장부를 덮고 당신을 바라본다.\n\n"이곳까지 찾아왔다는 건, 평범한 용건은 아니겠군요."`,
        alternateGreetings: [
          `${v["name"]!}이/가 주변을 살핀 뒤 낮게 말한다.\n\n"여기서는 아무 이름이나 크게 부르지 않는 게 좋아요."`,
        ],
        messageExample: `<START>\n{{user}}: 이 도시에서 무슨 일이 있었지?\n${v["name"]!}: 기록에 남은 일과 사람들이 기억하는 일은 꼭 같지 않아요.`,
        systemPrompt: roleplayRules,
        tags: ["롤플레잉", "NPC", "세계관"],
        rating: "sfw",
      };
    },
  },
];
